/**
 * # dxfBridge.conformantSkeleton.handles (DXF-CONFORMANT-SKELETON — by-construction)
 *
 * The handle-rewriting / handle-minting MACHINERY for the conformant-skeleton
 * back-fill, factored out of `dxfBridge.conformantSkeleton.ts` so the main file
 * stays under the size guard and so the three by-construction guarantees live in
 * one auditable place:
 *
 *   1. {@link HANDLE_FIELD_CLASS} — the CANONICAL classification of EVERY
 *      handle-bearing field across the entity / record schemas (`*Handle` /
 *      `*Handles` + the two `{code, handle}[]` shapes). A vitest census GATE
 *      (`dxfBridge.conformantSkeleton.test.ts`) derives the field set straight
 *      from `model.ts` + `arrayAssoc.ts` and asserts it EQUALS the keyset of
 *      this map — so adding a new handle field ANYWHERE breaks the test until it
 *      is classified here. This converts the per-round "we forgot field X"
 *      misses into a standing test break (mirrors the repo's
 *      httpErrorsTaxonomyGate / `satisfies Record<…>` + assertNever pattern).
 *
 *   2. {@link rewriteEntityHandlesGeneric} — a GENERIC recursive walk that
 *      rewrites every `/Handle$/` (string) / `/Handles$/` (string[]) field and
 *      every `{code, handle}[]` entry on a deep-cloned, re-minted entity through
 *      the old-id → new-hex `idMap`. It replaces BOTH the previous hand-listed
 *      `rewriteEntitySiblingHandles` AND the dead `rewriteMleaderHandles` (which
 *      read a `geometry` property that does not exist on the model). One
 *      traversal closes clippingBoundaryHandle, the entire MLEADER handle
 *      family, INSERT `attributes[].handle`, frozenLayerHandles and
 *      layerOverrideHandles — immune to path-shape drift.
 *
 *   3. {@link remintRecordOwnHandles} — one pass that mints / collision-checks
 *      every RECORD-own group-5 handle (`blocks[*].handle`,
 *      `blockRecords[*].handle`, `layouts[*].handle`, the four style-table
 *      record handles) against the skeleton handle set and rewrites the
 *      references that point AT those records through the remint map.
 *
 * Source of truth: Autodesk DXF reference + ezdxf 1.4.3 reference output.
 */
import {
  isHandleArrayKey,
  isSingleHandleKey,
  type HandleMinter,
} from "./dxfBridge.annotativeExport";
import { isLayoutBlockName } from "./blockNaming";
import type {
  BlockRecord,
  DrawingDocument,
  Layout,
} from "./model";

/** A valid DXF handle is a non-empty string of hex digits. */
const HEX_HANDLE_RE = /^[0-9A-Fa-f]+$/;
export function isHexHandle(value: string): boolean {
  return value.length > 0 && HEX_HANDLE_RE.test(value);
}

/**
 * The four handle-reference CLASSES that decide how a handle field is rewritten
 * when its OWNING entity is re-minted by the conformant-skeleton pass:
 *
 *   - `'sibling'` — a soft/hard pointer to ANOTHER reminted ENTITY in the same
 *     document (e.g. a HATCH boundary 330 → the source LINE, a viewport clip 340
 *     → the boundary LWPOLYLINE, an MLEADER 330 → its ATTDEF). Rewritten THROUGH
 *     the entity-occurrence `idMap`: a hit maps to the sibling's new hex handle;
 *     a non-hex id with no map entry is a truly-dangling ref → DROPPED (never
 *     emitted as an invalid non-hex handle).
 *   - `'own'`   — the entity's / record's OWN group-5 handle (the thing the
 *     writer emits as `5 <handle>`). Same rewrite as `'sibling'` (it IS the
 *     occurrence handle for nested-own handles like `attributes[].handle`); the
 *     top-level `entity.id` own handle is stamped by the caller and excluded
 *     from the walk.
 *   - `'table'`  — a pointer to a TABLES-section record the SKELETON owns or that
 *     is external structural state (STYLE / LTYPE / BLOCK_RECORD / LAYER /
 *     MATERIAL / plot-style). NOT an entity occurrence → NEVER idMap-mapped
 *     (mapping it would corrupt a valid structural ref into an entity handle).
 *     Preserved verbatim when hex; dropped when non-hex+unmapped (a dangling
 *     non-hex structural ref is invalid under strict ezdxf either way).
 *   - `'object'` — a pointer to an OBJECTS-section object (extension dictionary,
 *     representation-data record, AcDbAssoc* graph, reactor). Same
 *     preserve-verbatim-or-drop rule as `'table'` — these objects are not
 *     entity occurrences, so they are never idMap-mapped.
 *
 * The `'table'` / `'object'` guard is the safety net the prompt calls out: the
 * generic walk must NOT blindly idMap-map a structural ref. The classification
 * is consulted by basename (the field's leaf key) so the same key has ONE class
 * wherever it appears.
 */
export type HandleFieldClass = "sibling" | "own" | "table" | "object";

/**
 * Canonical classification of EVERY handle-bearing field name across the
 * entity + record schemas (model.ts + arrayAssoc.ts). The vitest census gate
 * asserts the keyset of this object EQUALS the set of `*Handle` / `*Handles`
 * field names declared in those schemas (plus the two `{code, handle}[]`
 * carriers, whose leaf key is `handle`). Adding/renaming a handle field in the
 * model breaks the gate until it is classified here.
 *
 * `satisfies Record<HandleFieldName, HandleFieldClass>` makes the value a
 * compile-time-checked total map; the gate makes it a runtime-checked total map
 * against the live schema census.
 */
export const HANDLE_FIELD_CLASS = {
  // ── Entity common base (drawingEntityBaseSchema) ──
  ownerHandle: "own", // 330 — owner BLOCK_RECORD (wired by the caller per space)
  materialHandle: "table", // 347 — AcDbMaterial
  plotStyleHandle: "table", // 390 — plot-style-name object
  extensionDictionaryHandle: "object", // 360 — entity ext dictionary
  // ── MLEADER family ──
  lineTypeHandle: "table", // 340 — LTYPE record (leader-line)
  attDefHandle: "sibling", // 330 — ATTDEF sibling entity (block-attr ref)
  styleHandle: "table", // 340 — TextStyle record (mtext data)
  leaderStyleHandle: "table", // 340 body — MLEADERSTYLE record (leader style); a
  // structural style ref (NOT an entity occurrence) → preserve verbatim when hex,
  // never idMap-remapped. Authored value is a synthetic hex minted ABOVE the
  // reminted population (stampMLeaderLeaderStyleHandles), so it survives verbatim.
  blockRecordHandle: "table", // 341/344 — BLOCK_RECORD record
  // ── MLEADERSTYLE record (styleTables.multileaderStyles) ──
  // SL-5 F6 — the source MLEADERSTYLE object's group-5 handle, REUSED as the
  // re-emitted object's handle for byte-stable save→reopen→save. A pointer to an
  // OBJECTS-section object (not an entity occurrence) → preserved verbatim when
  // hex, NEVER idMap-remapped (matches the reuse contract).
  sourceHandle: "object",
  // ── DIMENSION DSTYLE arrowhead override (SL-11) ──
  // 342/343/344 DSTYLE soft-pointer handle to the arrowhead BLOCK_RECORD, the
  // IMPORTED provenance twin of `styleOverrides.dimblk*`. A structural
  // BLOCK_RECORD ref (like `blockRecordHandle`) → `'table'`: kept verbatim when
  // hex, FOLLOWED through the record-remint `remap` (`remapEntityHandleReferences`)
  // when the arrow record is reminted on a conformant save, dropped when non-hex.
  dimblkHandle: "table",
  dimblk1Handle: "table",
  dimblk2Handle: "table",
  // ── ACAD_TABLE ──
  // 342 — the TABLESTYLE object in the OBJECTS section (ezdxf files the class
  // under `dict_name="ACAD_TABLESTYLE"`). An OBJECTS-section pointer, NOT an
  // entity occurrence, so it is never idMap-mapped: kept verbatim when hex,
  // dropped when non-hex. It FOLLOWS a carried TABLESTYLE to its minted handle
  // through the record-remint remap (`remapEntityHandleReferences`), the same
  // way `dimblk*Handle` follows a reminted arrowhead BLOCK_RECORD.
  //
  // It reached this map late (@codex round 17 P2) because the census gate read
  // `model.ts` / `arrayAssoc.ts` / `entityBase.ts` only, and this field is
  // declared in `entityPolylineTableOle.ts` — so the gate's "adding a handle
  // field ANYWHERE breaks the test until it is classified" promise had a hole
  // in it, and the field was silently taking `classOf`'s `'sibling'` fallback.
  // The census now reads that file too; the fallback is unreachable again.
  tableStyleHandle: "object",
  // ── HATCH ──
  sourceBoundaryHandles: "sibling", // 330[] — source boundary entities
  // ── INSERT representation ──
  representationDataHandle: "object", // ACDB_BLOCKREPRESENTATION_DATA object
  // ── ARRAY assoc graph (arrayAssoc.ts) ──
  entityProviderHandle: "sibling", // item display block-reference entity
  repBodyHandle: "object", // AcDbAssocArrayModifyActionBody object
  actionBodyHandle: "object", // ACDBASSOCARRAYACTIONBODY object
  arrayParametersHandle: "object", // ACDBASSOCARRAYRECTANGULARPARAMETERS object
  // AW-1 (docs/MASTERPLAN.md AW-1) — the owning ACDBASSOCACTION object's own
  // handle (round-trip linkage the write-back encoder locates the named
  // value-param form through). Same class as its sibling handles above.
  actionHandle: "object", // ACDBASSOCACTION object
  dependencyHandles: "object", // AcDbAssocDependency[] objects
  // AW-0 (docs/MASTERPLAN.md AW-0) — `assocArrayCarrierObjectSchema`
  // (arrayAssoc.ts) typed graph-carrier field: on an ACDBASSOCDEPENDENCY
  // record this is the FK the dependency carries OUT to the referenced
  // SOURCE ENTITY (a sibling drawing entity — e.g. the arrayed CIRCLE), not
  // an OBJECTS-section record. Mirrors `entityProviderHandle` above.
  dependencyRefHandle: "sibling", // ACDBASSOCDEPENDENCY → referenced source entity
  // ── VIEWPORT ──
  frozenLayerHandles: "table", // 331[] — LAYER records
  // PRESERVE-SOURCE round-trip carrier (@codex #514 P2 "Classify source 331
  // handles for reminting") — the FULL verbatim native 331 snapshot; same target
  // table as frozenLayerHandles, so it MUST follow a LAYER-record remint through
  // the SAME 'table' class + the generic *Handles suffix walk
  // (`remapEntityHandleReferences`/`collectHandlesDeep`), otherwise a conformant
  // save of a provenance-less clone could emit a stale/aliasing 331 handle.
  sourceFrozenLayerHandles: "table", // 331[] verbatim source — LAYER records
  // `layerOverrideHandles` is a `{code, handle}[]` (335/343/344/91 → LAYER /
  // override OBJECTS). The FIELD name is classed here for census completeness;
  // the per-ENTRY `handle` is rewritten as `'object'` by `rewriteArrayEntries`.
  layerOverrideHandles: "object",
  clippingBoundaryHandle: "sibling", // 340 — paperspace boundary entity
  // ── Generic own group-5 handle (ATTRIB child, ATTDEF entity, and the
  //    record-own handles on block / blockRecord / layout / layer / style) ──
  handle: "own",
  // T9b — a BLOCK definition's matching ENDBLK own group-5 (blockDefinition
  // schema). An OWN handle (emitted as `5 <h>` on the ENDBLK), never a
  // reference: minted / collision-checked by `remintRecordOwnHandles`, harvested
  // by the own-handle census, excluded from the back-ref remap.
  endblkHandle: "own",
  // T9b — an INSERT's terminating SEQEND own group-5 (blockInsertEntitySchema).
  // Same OWN contract as `endblkHandle`: minted by the skeleton's entity pass,
  // harvested by the own-handle census, never remapped as a reference.
  seqendHandle: "own",
  // ── Record-level refs (blockDefinition / blockRecord / layout) ──
  layoutHandle: "object", // BLOCK_RECORD 340 → LAYOUT object
  reactorHandles: "object", // BLOCK_RECORD 102/ACAD_REACTORS 330[]
  ownerBlockRecordHandle: "table", // LAYOUT 330 → owning BLOCK_RECORD
  dictionaryOwnerHandle: "object", // LAYOUT 330 → owning ACAD_LAYOUT dictionary
} satisfies Record<string, HandleFieldClass>;

export type HandleFieldName = keyof typeof HANDLE_FIELD_CLASS;

/**
 * T9b — the OWN group-5 handle keys (emitted as `5 <h>`, never a reference):
 * the bare `handle`, a BLOCK's `endblkHandle`, an INSERT's `seqendHandle`. Shared
 * by the own-handle census (`collectTypedOwnHandles`), the record back-ref remap
 * and the entity reference remap so none of them can re-stamp an own handle.
 */
export const OWN_HANDLE_KEYS: ReadonlySet<string> = new Set([
  "handle",
  "endblkHandle",
  "seqendHandle",
]);

/**
 * Resolve the class of a handle field by its LEAF key (basename). A `Handle`-/
 * `Handles`-suffixed key (or the bare `handle` own-key) that is NOT in the
 * canonical map is treated as `'sibling'` so a newly-added field NEVER silently
 * leaks an un-rewritten non-hex id (worst case: a valid structural ref is
 * dropped, which is loud + caught by the census gate before it ships). The
 * census gate guarantees this fallback is unreachable in practice.
 */
function classOf(key: string): HandleFieldClass {
  if (key in HANDLE_FIELD_CLASS) {
    return (HANDLE_FIELD_CLASS as Record<string, HandleFieldClass>)[key];
  }
  return "sibling";
}

// `isSingleHandleKey` / `isHandleArrayKey` are the SHARED shape-classifiers
// imported from dxfBridge.annotativeExport (SINGLE SOURCE OF TRUTH) so the
// census/seed walk (`collectHandlesDeep`) and this structural rewrite cannot
// drift on what counts as a single-handle vs handle-array field.

/**
 * Rewrite ONE handle string for a field of class `cls` through `idMap`:
 *   - `'sibling'` / `'own'` — map via idMap (old entity id → new hex). A hit
 *     returns the mapped hex; an already-hex value is kept verbatim (an external
 *     ref); a non-hex value with no map entry returns `undefined` (DROP).
 *   - `'table'` / `'object'` — NEVER idMap-mapped. An already-hex structural ref
 *     is kept verbatim; a non-hex unmapped ref returns `undefined` (DROP — a
 *     dangling non-hex structural handle is invalid under strict ezdxf, and we
 *     never invent one).
 *
 * Returns `undefined` to signal the caller should DROP the field (delete the
 * key / omit the array entry) rather than emit an invalid handle.
 */
function rewriteOneHandle(
  value: string,
  cls: HandleFieldClass,
  idMap: ReadonlyMap<string, string>,
): string | undefined {
  if (cls === "sibling" || cls === "own") {
    // DXF handles are CASE-INSENSITIVE — the occurrence `idMap` is seeded
    // LOWERCASED (conformantSkeleton.ts `scope.set(oldId.toLowerCase(), …)`), so
    // a sibling ref "a1" must resolve the same entry as the id "A1" (Axis A2:
    // a case-mismatched ref otherwise misses the map, stays hex → dangling).
    const mapped = idMap.get(value.toLowerCase());
    if (mapped !== undefined) return mapped;
    if (isHexHandle(value)) return value;
    return undefined;
  }
  // 'table' / 'object' — preserve verbatim when hex, drop when non-hex.
  return isHexHandle(value) ? value : undefined;
}

/**
 * T9 defect 5 — handle-ARRAY fields whose members MUST resolve to an in-document
 * reminted entity (a hit in the occurrence `idMap`), with NO hex-passthrough
 * fallback. The HATCH `sourceBoundaryHandles` (330[] soft pointers to the source
 * boundary entities the associative hatch was created from) is the only such
 * field: every legitimate source-boundary ref points at an entity that the
 * conformant-skeleton pass re-mints, so it ALWAYS resolves through the idMap. A
 * member that does NOT resolve is a TRULY-DANGLING ref (e.g. a WBLOCK / OBJECTS
 * deep-clone of an imported associative hatch carries its ORIGINAL hex source
 * handles into a provenance-less from-scratch wrapper whose boundary entity is
 * absent) — emitting it as a `330` ships a dangling soft-pointer that resolves to
 * nothing. ezdxf TOLERATES a dangling 330, so the by-construction guard is to
 * DROP the unresolvable member here (the writer re-derives group 97 from the
 * surviving array length; 0 survivors ⇒ no 97/330 block ⇒ the boundary path is
 * effectively non-associative — the plan's "valid resolved ref, else
 * non-associative safe fallback").
 *
 * Scoped to this ONE field on purpose: the GENERIC `rewriteOneHandle` hex-keep
 * behaviour is correct for every OTHER sibling/table/object handle-array
 * (`frozenLayerHandles` → LAYER records, `reactorHandles` / `dependencyHandles` →
 * OBJECTS, etc.), where an already-hex value is a legitimate external structural
 * ref that must be PRESERVED, not dropped. Changing the general behaviour would
 * regress those — so the strict-resolve rule keys off the field NAME.
 */
const MUST_RESOLVE_HANDLE_ARRAY_KEYS: ReadonlySet<string> = new Set([
  "sourceBoundaryHandles",
]);

/**
 * Rewrite ONE member of a `MUST_RESOLVE_HANDLE_ARRAY_KEYS` field through `idMap`,
 * returning the mapped hex ONLY when the member resolves to an in-document
 * reminted entity. An unresolved member (hex or non-hex) returns `undefined`
 * (DROP) — no hex-passthrough, unlike the generic {@link rewriteOneHandle}
 * `'sibling'` rule. Mirrors the case-insensitive lookup of the occurrence idMap.
 */
function rewriteStrictResolveMember(
  value: string,
  idMap: ReadonlyMap<string, string>,
): string | undefined {
  return idMap.get(value.toLowerCase());
}

/**
 * GENERIC structural handle rewrite (replaces the hand-listed
 * `rewriteEntitySiblingHandles` + the dead `rewriteMleaderHandles`).
 *
 * Walks the deep-cloned, already-re-minted entity recursively and rewrites,
 * through {@link rewriteOneHandle} (consulting {@link HANDLE_FIELD_CLASS} per
 * leaf key):
 *   - every `string` value under a `handle` / `*Handle` key,
 *   - every `string[]` value under a `*Handles` key (dropping members that map
 *     to `undefined`),
 *   - every `{code:number, handle:string}` object entry inside ANY array (the
 *     viewport `layerOverrideHandles` shape) — rewriting its `handle` field,
 *   - every XData `{code:1005, value}` group inside ANY array (a 1005 handle
 *     reference carried verbatim in `extraGroups` / `rawGroups`) — treated as a
 *     `'sibling'` ref through the idMap.
 *
 * The entity's OWN top-level `id` (group-5) and its `ownerHandle` (group 330,
 * wired to the per-space BLOCK_RECORD) are set by the CALLER and must NOT be
 * re-touched here — the caller passes `skipKeys` (`{id, ownerHandle}` at the
 * TOP level only) so a nested `ownerHandle`/`id` deeper in the graph still
 * rewrites normally.
 */
/**
 * The minter the rewrite passes accept. A plain `() => string` is the LEGACY
 * shape (drop-on-collision for kept-verbatim handles); a {@link HandleMinter}
 * (the conformant-skeleton path) ALSO carries `has`/`reserve` so an already-hex
 * NESTED OWN handle that COLLIDES with a skeleton / sibling handle is reminted
 * instead of emitted as a duplicate group-5 (BUG 3).
 */
type RewriteMinter = (() => string) | HandleMinter;

export function rewriteEntityHandlesGeneric(
  entity: unknown,
  idMap: ReadonlyMap<string, string>,
  mintHandle?: RewriteMinter,
): void {
  walkNode(entity, idMap, /* topLevel */ true, mintHandle);
}

/**
 * Rewrite ONE NESTED OWN group-5 handle (an `attributes[].handle` ATTRIB, a
 * typed ATTDEF child `handle`, …) so the emitted record always carries a unique
 * hex group-5:
 *   - a hit in `idMap` (the entity-occurrence map) → the mapped hex,
 *   - an already-hex value with no map entry → kept verbatim,
 *   - an empty / non-hex value with a `mintHandle` available → a FRESH hex
 *     handle (so a handle-less / `attrib-sentinel`-style ATTRIB is no longer
 *     dropped to a handle-less record that strict ezdxf rejects). The old value
 *     (when non-empty) is recorded in `idMap` so a sibling ref to it follows.
 *   - no `mintHandle` (legacy callers) → fall back to the generic
 *     {@link rewriteOneHandle} (drop on non-hex).
 *
 * FIX A (round-9 #1) — `rewriteEntityHandlesGeneric` previously treated a nested
 * own `handle` as a `'sibling'`/`'own'` ref through the TOP-LEVEL idMap (which
 * only carries L1+L2 entity ids). A non-hex attribute handle therefore mapped to
 * nothing → was DELETED → `drawingAttribToDxfAttrib` emitted no group-5 →
 * handle-less ATTRIB. Minting a fresh hex here gives every nested ATTRIB/ATTDEF a
 * unique own group-5.
 */
function rewriteNestedOwnHandle(
  value: string,
  idMap: ReadonlyMap<string, string>,
  mintHandle: RewriteMinter | undefined,
): string | undefined {
  const reservable = isHandleMinter(mintHandle) ? mintHandle : undefined;
  const mint = (): string | undefined => {
    if (!mintHandle) return undefined;
    const minted = mintHandle();
    // Record old→new so a SIBLING ref pointing at this nested own-handle (e.g.
    // an MLEADER `attDefHandle` → an ATTDEF, or an XData 1005 → an ATTRIB)
    // resolves to the freshly minted hex. `idMap` is the live occurrence map
    // (mutated in place by the caller's scope). When the same OLD value already
    // mapped (a DIFFERENT own handle that shared this value, or a foreign owner),
    // last-writer-wins is acceptable: duplicate source own-handles are malformed,
    // and the per-occurrence emitted handle is always the freshly minted unique
    // one — the map only resolves sibling refs.
    if (value.length > 0) {
      (idMap as Map<string, string>).set(value.toLowerCase(), minted);
    }
    return minted;
  };

  if (isHexHandle(value)) {
    // BUG 3 (round-11 P2) — an already-hex NESTED OWN handle is the entity's OWN
    // group-5 identity, NOT a sibling reference. It is kept verbatim ONLY when it
    // does NOT collide with a skeleton / sibling handle the minter tracks. A
    // from-scratch block ATTDEF/ATTRIB authored with handle "18" (= skeleton
    // *Model_Space BLOCK header) was previously kept verbatim → DUPLICATE group-5
    // once the typed ATTDEFs are emitted. The collision check is consulted BEFORE
    // the idMap lookup: returning a FOREIGN `idMap[value]` (set by a DIFFERENT
    // own handle that minted off the SAME colliding value, reachable via the
    // composed block scope) would re-emit that other entity's handle → a second
    // duplicate. A hex+free value is RESERVED + kept; a hex+colliding value is
    // REMINTED fresh (never shared through the idMap).
    if (reservable === undefined) {
      // Legacy caller (no collision tracker) — keep verbatim as before.
      return value;
    }
    if (reservable.reserve(value)) {
      // Hex + free → reserved + kept verbatim (the common, non-colliding case).
      return value;
    }
    // Hex but COLLIDING → mint a fresh, unique hex (never the foreign idMap hit).
    return mint();
  }
  // Non-hex / empty own handle. FIX A (round-9 #1) — a value PRE-KEYED in the
  // occurrence map (the entity's own `id` reused as the `handle`, or a sibling
  // that already minted it) follows that mapping; otherwise mint a fresh hex so
  // the nested record never emits a handle-less / non-hex group-5.
  const mapped = idMap.get(value.toLowerCase());
  if (mapped !== undefined) return mapped;
  return mint();
}

/** Narrow a `RewriteMinter` to the collision-aware {@link HandleMinter}. */
function isHandleMinter(
  minter: RewriteMinter | undefined,
): minter is HandleMinter {
  return (
    typeof minter === "function" &&
    typeof (minter as HandleMinter).has === "function" &&
    typeof (minter as HandleMinter).reserve === "function"
  );
}

function walkNode(
  node: unknown,
  idMap: ReadonlyMap<string, string>,
  topLevel: boolean,
  mintHandle?: RewriteMinter,
): void {
  if (node === null || typeof node !== "object") return;

  if (Array.isArray(node)) {
    // FIX B (round-9 #2) — `rewriteArrayEntries` returns the entry objects it
    // CONSUMED (a `{code, handle}` viewport override or a `{code:1005, value}`
    // XData ref). We must NOT recurse `walkNode` into those: the recursion would
    // re-match their `handle`/`value` key as a nested OWN/sibling field and
    // re-rewrite it through the idMap — double-processing that wrongly redirects a
    // structural OBJECT pointer onto a reminted entity (layerOverrideHandles).
    const consumed = rewriteArrayEntries(node, idMap);
    for (const item of node) {
      if (consumed.has(item)) continue;
      walkNode(item, idMap, false, mintHandle);
    }
    return;
  }

  const record = node as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    // The caller stamps the entity's own `id` (group-5) and its `ownerHandle`
    // (per-space BLOCK_RECORD) at the TOP level — never re-touch them here.
    if (topLevel && (key === "id" || key === "ownerHandle")) continue;

    if (typeof value === "string" && isSingleHandleKey(key)) {
      const cls = classOf(key);
      // A NESTED OWN group-5 handle is the bare `handle` key ONLY (the true
      // emitted `5 <handle>` of an ATTRIB/ATTDEF child). The other `'own'`-class
      // key, `ownerHandle`, is a 330 REFERENCE to the owning BLOCK_RECORD — it
      // must NEVER be MINTED (inventing a fresh hex for an empty/dangling owner
      // would fabricate a non-existent owner), so it follows the reference path.
      const isNestedOwnGroup5 = cls === "own" && key === "handle";
      // FIX A (round-9 #1, EMPTY-handle correction round-10 BUG 1) — a nested own
      // group-5 `handle` MUST be reminted to a fresh hex whenever it is empty /
      // non-hex and a minter is available — never dropped to a handle-less
      // ATTRIB/ATTDEF. `createAttdefMutation` (+ the schema default) INIT an
      // ATTDEF/ATTRIB with `handle:""`, so the EMPTY case is the COMMON
      // from-scratch case: gating on `value.length > 0` SKIPPED it → no group-5 →
      // strict readers reject the handle-less nested entity. An empty own handle
      // therefore enters the minter exactly like a top-level entity always gets a
      // minted hex.
      if (isNestedOwnGroup5) {
        const next = rewriteNestedOwnHandle(value, idMap, mintHandle);
        if (next === undefined) delete record[key];
        else record[key] = next;
        continue;
      }
      // A `*Handle` REFERENCE (sibling/table/object, or the `ownerHandle` 330)
      // keeps the generic preserve-or-drop rule — an EMPTY reference is simply
      // nothing to rewrite (and is NEVER minted).
      if (value.length > 0) {
        const next = rewriteOneHandle(value, cls, idMap);
        if (next === undefined) delete record[key];
        else record[key] = next;
      }
      continue;
    }

    if (
      Array.isArray(value) &&
      isHandleArrayKey(key) &&
      value.every((h) => typeof h === "string")
    ) {
      // A STRING[] handle-array field (sourceBoundaryHandles / frozenLayerHandles
      // / reactorHandles / dependencyHandles). Rewrite each member, dropping a
      // member that maps to undefined (keeps the array count↔refs consistent —
      // e.g. the writer re-derives HATCH group-97 from the array length). The
      // `value.every(isString)` guard excludes the `{code, handle}[]`
      // `layerOverrideHandles` carrier (also `*Handles`-named) — that falls
      // through to the recursive `walkNode` → `rewriteArrayEntries` below.
      const cls = classOf(key);
      // T9 defect 5 — a `MUST_RESOLVE_HANDLE_ARRAY_KEYS` field
      // (`sourceBoundaryHandles`) DROPS any member that does not resolve to an
      // in-document reminted entity (no hex-passthrough): a hex-shaped dangling
      // source-boundary ref would otherwise survive `rewriteOneHandle`'s
      // `'sibling'` hex-keep rule and ship as a dangling `330`. Every OTHER
      // handle-array keeps the generic preserve-or-drop rule.
      const strictResolve = MUST_RESOLVE_HANDLE_ARRAY_KEYS.has(key);
      const rewritten: string[] = [];
      for (const h of value as string[]) {
        if (h.length === 0) continue;
        const next = strictResolve
          ? rewriteStrictResolveMember(h, idMap)
          : rewriteOneHandle(h, cls, idMap);
        if (next !== undefined) rewritten.push(next);
      }
      record[key] = rewritten;
      continue;
    }

    if (value !== null && typeof value === "object") {
      walkNode(value, idMap, false, mintHandle);
    }
  }
}

/**
 * Rewrite the two array-of-object handle carriers in place:
 *   - `{code:number, handle:string}` — the viewport `layerOverrideHandles`
 *     shape. The `handle`'s class is `'handle' → 'own'`, but a layer-override
 *     points at a LAYER / override OBJECT, NOT an entity occurrence, so it must
 *     NOT be idMap-mapped. We classify these by their GROUP CODE: 343/344 (hard
 *     pointer to override objects) and 335/91 (soft pointer) all reference
 *     OBJECTS-section override records → `'object'` (preserve-verbatim-or-drop).
 *   - `{code:1005, value:string}` — an XData handle reference carried verbatim
 *     in `extraGroups` / leader-line/leader/context `rawGroups`. A 1005 is a
 *     soft pointer to a SIBLING entity (AutoCAD's XDATA handle group) → rewrite
 *     through the idMap like a `'sibling'`.
 */
function rewriteArrayEntries(
  arr: unknown[],
  idMap: ReadonlyMap<string, string>,
): ReadonlySet<unknown> {
  // FIX B (round-9 #2) — the entry objects this function CONSUMED (a viewport
  // `{code, handle}` override or a `{code:1005, value}` XData ref). The caller
  // MUST NOT recurse `walkNode` into these — a recursion would re-match their
  // `handle`/`value` key as a nested OWN/sibling field and re-rewrite it through
  // the idMap, wrongly redirecting a structural OBJECT pointer onto a reminted
  // entity (e.g. `layerOverrideHandles:[{code:343,handle:"2A1"}]` with
  // idMap{'2a1'→reminted-entity}).
  const consumed = new Set<unknown>();
  for (let i = 0; i < arr.length; i += 1) {
    const item = arr[i];
    if (item === null || typeof item !== "object") continue;
    const obj = item as Record<string, unknown>;
    const code = obj.code;

    // Viewport layerOverrideHandles `{code, handle}` entry.
    if (
      typeof code === "number" &&
      typeof obj.handle === "string" &&
      obj.handle.length > 0
    ) {
      // 343/344 = hard-pointer override objects; 335/91 = soft pointer. All
      // reference OBJECTS-section override records, never entity occurrences.
      const next = rewriteOneHandle(obj.handle, "object", idMap);
      if (next === undefined) {
        // A non-hex unmapped override handle is invalid — null the entry's
        // handle so the caller's array-filter can drop it. We cannot delete a
        // required `handle` and keep a valid pair, so blank it; the export
        // path's `layerOverrideHandles` emit guards on a non-empty handle.
        obj.handle = "";
      } else {
        obj.handle = next;
      }
      consumed.add(item);
      continue;
    }

    // XData 1005 handle group `{code:1005, value}` carried verbatim.
    if (code === 1005 && typeof obj.value === "string" && obj.value.length > 0) {
      const next = rewriteOneHandle(obj.value, "sibling", idMap);
      // Drop a dangling non-hex 1005 (mark with empty value; the array filter
      // below removes blanked entries so count↔refs stays consistent).
      obj.value = next ?? "";
      consumed.add(item);
      continue;
    }
  }
  // Drop blanked viewport-override / XData-1005 entries (a non-hex unmapped ref
  // that we could not rewrite) so no invalid handle survives.
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    const item = arr[i];
    if (item === null || typeof item !== "object") continue;
    const obj = item as Record<string, unknown>;
    const code = obj.code;
    if (typeof code === "number" && "handle" in obj && obj.handle === "") {
      arr.splice(i, 1);
    } else if (code === 1005 && obj.value === "") {
      arr.splice(i, 1);
    }
  }
  return consumed;
}

/**
 * The DXF group codes carrying a record's OWN handle inside a raw entity-group
 * leaf: 5 (general) + 105 (DIMSTYLE). 1005 is an XData REFERENCE, not an own
 * handle, so it is rewritten through the occurrence map (sibling), not minted.
 */
const RAW_OWN_HANDLE_CODES = new Set<number>([5, 105]);

/**
 * round-16 BUG 3 — the SINGLE SOURCE OF TRUTH set of DXF group codes that carry a
 * handle REFERENCE inside a raw entity-group leaf (a soft/hard pointer or owner
 * handle to ANOTHER record), so {@link rewriteRawEntityGroupRefs} remaps EVERY one
 * through the occurrence + raw-own remint maps — not just the legacy 1005.
 *
 * Per the Autodesk DXF reference, the reference-handle code ranges are:
 *   - 320-329 — arbitrary object handles,
 *   - 330-339 — soft-pointer handles (e.g. 330 owner / reactor),
 *   - 340-349 — hard-pointer handles (e.g. 340 LAYOUT / boundary ref),
 *   - 350-359 — soft-owner handles,
 *   - 360-369 — hard-owner handles (e.g. 360 ext-dictionary / hard-owner),
 *   - 1005     — XData handle reference (soft pointer to a sibling).
 *
 * The OWN-handle codes 5 / 105 are DELIBERATELY EXCLUDED — {@link RAW_OWN_HANDLE_CODES}
 * already mints those in {@link remintRawEntityGroupHandles}; treating an own
 * handle as a reference here would re-point the record's own group-5 onto another
 * record. A raw leaf code in this set that is NOT 1005 and NOT a 330-owner is a
 * pure sibling/structural REFERENCE: a hit in the remint maps → its new hex; an
 * already-hex value → kept verbatim; a dangling non-hex ref → "0" (the DXF null
 * handle ezdxf accepts, never an invalid handle).
 */
const DXF_HANDLE_REFERENCE_CODES: ReadonlySet<number> = new Set<number>([
  ...Array.from({ length: 50 }, (_, i) => 320 + i), // 320-369 (incl 330-369 + 320-329)
  1005,
]);

/**
 * round-16 BUG 3 — the soft-pointer OWNER group code (330). A raw entity's FIRST
 * 330 is its STRUCTURAL OWNER (the owning space / block-record handle), NOT a
 * sibling reference; nulling it to "0" when it does not resolve via the remint
 * maps would ORPHAN the entity. The caller threads the structural owner handle
 * for each raw stream so a non-resolving FIRST 330 is set to that owner instead.
 */
const DXF_OWNER_REFERENCE_CODE = 330;

/**
 * FIX C (round-9 #3 / SG-2) — remint every RAW entity-group OWN handle so a raw
 * ENTITIES-/BLOCKS-section child (an unsupported XRECORD / ACAD_TABLE / SPLINE /
 * … preserved verbatim in `document.rawEntityGroups` or `block.rawEntityGroups`)
 * always emits a VALID, UNIQUE hex group-5.
 *
 * The PASS-2 entity remint walks only TYPED entities, so a raw child's
 * `{code:5, value}` own handle was emitted VERBATIM:
 *   - a non-hex own handle ("spl-1") is a HARD ezdxf `Invalid handle` reject,
 *   - a low own handle ("18") COLLIDES with a skeleton BLOCK / record header →
 *     ezdxf `Found non-unique entity handle`.
 *
 * For every raw group we:
 *   - mint a fresh hex for an own `{code:5|105}` handle that is empty / non-hex /
 *     already used (recording old→new in `ownRemap` so a SIBLING reference to it
 *     follows), keeping an already-valid+unique hex verbatim;
 *   - rewrite every `{code:1005}` XData handle reference through the occurrence
 *     `idMap` (a SIBLING ref to a reminted typed entity) THEN `ownRemap` (a ref
 *     to another reminted raw child) — dropping a dangling non-hex ref's value to
 *     "0" (ezdxf accepts the null handle, never an invalid one).
 *
 * `used` is seeded from the caller's document-wide handle population and grows as
 * each minted/kept own handle is registered, so two raw children never share a
 * handle. `ownRemap` accumulates ACROSS calls (document + every block) so a
 * cross-stream reference resolves. Mutates the raw groups in place.
 */
export function remintRawEntityGroupHandles(
  rawGroups: { entityType: string; groups: { code: number; value: string }[] }[]
    | undefined,
  idMap: ReadonlyMap<string, string>,
  used: Set<string>,
  ownRemap: Map<string, string>,
  mintHandle: () => string,
): void {
  if (!rawGroups) return;
  // PASS 1 — mint/keep each raw child's OWN handle, recording old→new in ownRemap.
  for (const raw of rawGroups) {
    let ownSeen = false;
    for (const g of raw.groups) {
      if (!RAW_OWN_HANDLE_CODES.has(g.code) || ownSeen) continue;
      ownSeen = true; // only the FIRST 5/105 is the record's own handle
      const old = g.value.trim();
      const lower = old.toLowerCase();
      const valid = isHexHandle(old) && !used.has(lower);
      if (valid) {
        used.add(lower);
        continue;
      }
      const minted = mintHandle();
      used.add(minted.toLowerCase());
      if (lower.length > 0) ownRemap.set(lower, minted);
      g.value = minted;
    }
    // round-15 BUG 2 — a raw entity that carries NO 5/105 group AT ALL never set
    // `ownSeen`, so the loop above minted nothing and the emitted record would
    // have NO group-5 → a handle-less ENTITIES/BLOCKS record (strict ezdxf
    // `readfile` reject / audit-fix). Mint a fresh hex and INSERT it at the FRONT
    // of `raw.groups`. `rawEntityGroupToDxfUnsupported` prepends the implicit
    // `0 <type>` header then spreads `raw.groups` verbatim, so an `unshift`ed
    // `{code:5}` lands right after the `0 <type>` and BEFORE the
    // `100 AcDbEntity` subclass marker — the slot AutoCAD/ezdxf require group-5.
    // No `ownRemap` entry is needed: there was no old own handle, so nothing
    // references this record by an old handle.
    if (!ownSeen) {
      const minted = mintHandle();
      used.add(minted.toLowerCase());
      raw.groups.unshift({ code: 5, value: minted });
    }
  }
}

/**
 * FIX C + round-16 BUG 3 — rewrite EVERY handle REFERENCE inside raw entity groups
 * through the combined occurrence + raw-own remint maps, AFTER every own handle is
 * minted (so a forward reference to a later raw child resolves). The references are
 * the FULL DXF reference-code set ({@link DXF_HANDLE_REFERENCE_CODES}: 320-369
 * soft/hard pointer + owner handles, plus 1005 XData), NOT just the legacy 1005 —
 * a raw sibling carrying a non-hex id in a 340 hard pointer or a non-hex 330
 * owner/ref previously leaked an INVALID/dangling handle into the "conformant"
 * output. Each reference resolves as:
 *   - a hit in `idMap` (a reminted TYPED entity) → its new hex,
 *   - else a hit in `ownRemap` (a reminted RAW child) → its new hex,
 *   - else an already-hex value → kept verbatim,
 *   - else (a dangling non-hex ref) → "0" (the DXF null handle — never invalid),
 *     EXCEPT the FIRST 330 (the structural OWNER): a non-resolving owner is set to
 *     `structuralOwnerHandle` (the owning space / block-record handle the caller
 *     threads in) so the raw entity stays correctly OWNED rather than orphaned.
 * The `{code:5|105}` OWN handles are NOT touched here (already minted in PASS 1).
 *
 * `structuralOwnerHandle` — the owning space / block-record hex handle for this
 * raw stream (`document.rawEntityGroups` → the model/paper space block-record;
 * `block.rawEntityGroups` → that block's record handle). Used ONLY as the fallback
 * for a FIRST-330 owner that does not resolve via the remint maps. May be
 * undefined (a transient / unknown-owner stream) → the owner then falls back to
 * the verbatim-or-"0" reference rule like any other code.
 */
export function rewriteRawEntityGroupRefs(
  rawGroups: { entityType: string; groups: { code: number; value: string }[] }[]
    | undefined,
  idMap: ReadonlyMap<string, string>,
  ownRemap: ReadonlyMap<string, string>,
  structuralOwnerHandle?: string,
): void {
  if (!rawGroups) return;
  for (const raw of rawGroups) {
    let ownSeen = false;
    let firstOwnerSeen = false;
    // round-17 BUG 2 — track `102` control-block nesting depth (mirrors the
    // canonical guard in dxf-io `parseLayoutEntry` / the table-record parser): a
    // `102 {…` opens a block, a `102 }` closes it. A raw entity can carry a
    // LEADING `102 {ACAD_REACTORS … 330 <reactor> … 102 }` control block whose
    // nested 330 is a REACTOR reference, NOT the structural owner. Only a 330 seen
    // OUTSIDE any `102` block (depth 0) is the structural owner / a remappable
    // top-level reference; 330/340/350/360 INSIDE a `102` block are reactor/dict
    // refs — they ARE still remapped through idMap/ownRemap (they are references),
    // but must NEVER be treated as the structural OWNER. Without the depth guard a
    // flat `firstOwnerSeen` wrongly latched the nested reactor 330 as the owner,
    // leaving the REAL top-level owner 330 (later in the record) stale or nulled
    // to "0" → an orphaned raw entity (corrupts from-scratch / WBLOCK exports with
    // raw reactor-bearing entities).
    let controlDepth = 0;
    for (const g of raw.groups) {
      if (g.code === 102) {
        const marker = g.value.trim();
        if (marker.startsWith("{")) controlDepth += 1;
        else if (controlDepth > 0) controlDepth -= 1; // a closing "}" marker
        continue; // the `102` boundary markers are never handle refs
      }
      if (RAW_OWN_HANDLE_CODES.has(g.code) && !ownSeen) {
        ownSeen = true; // skip the own handle (already minted)
        continue;
      }
      if (!DXF_HANDLE_REFERENCE_CODES.has(g.code)) continue;
      const old = g.value.trim();
      if (old.length === 0) continue;
      // The FIRST DEPTH-0 330 is the record's STRUCTURAL OWNER (owning space /
      // block-record); a non-resolving owner falls back to `structuralOwnerHandle`,
      // never "0". A 330 INSIDE a `102` block (controlDepth > 0) is a reactor/dict
      // reference, never the owner — it is remapped like any other reference below.
      const isStructuralOwner =
        g.code === DXF_OWNER_REFERENCE_CODE &&
        controlDepth === 0 &&
        !firstOwnerSeen;
      if (g.code === DXF_OWNER_REFERENCE_CODE && controlDepth === 0) {
        firstOwnerSeen = true;
      }
      const lower = old.toLowerCase();
      const mapped = idMap.get(lower) ?? ownRemap.get(lower);
      if (mapped !== undefined) {
        g.value = mapped;
        continue;
      }
      if (isHexHandle(old)) continue; // already-hex structural/sibling ref kept
      // Dangling non-hex reference.
      if (isStructuralOwner && structuralOwnerHandle !== undefined) {
        g.value = structuralOwnerHandle; // re-own to the real owning record
      } else {
        g.value = "0"; // DXF null handle (never an invalid non-hex handle)
      }
    }
  }
}

/**
 * SL-11 round-8 F3 — MLEADER handle-valued REFERENCE fields that do NOT follow the
 * `*Handle` naming convention, so {@link isSingleHandleKey} misses them and they
 * would NOT follow a reminted BLOCK_RECORD:
 *
 *   - `arrowheadBlock` — AcDbMLeader body group 342 (arrow_head_handle) on the
 *     MLEADER ENTITY and on each CONTEXT_DATA leader-line. It is a soft-pointer
 *     HANDLE to the arrowhead BLOCK_RECORD (same class as the DIMENSION
 *     `dimblk*Handle` twin), but the field is shared with an AUTHORED block NAME
 *     (the DIMENSION side split name↔handle into `dimblk` / `dimblk*Handle`;
 *     MLEADER kept ONE `arrowheadBlock`). When a conformant save reminds the
 *     arrow BLOCK_RECORD's handle, the DIMENSION `dimblk*Handle` twin follows via
 *     the generic `*Handle` walk below, but the MLEADER body-342 did NOT →
 *     an imported MLEADER arrow DANGLED after remint. Following it here reuses the
 *     SAME `remap`/`idMap` pass (no parallel walk): only a value present as a KEY
 *     (an old reminted RECORD handle) is substituted, so an AUTHORED NAME (never a
 *     key) is left verbatim — safe for the name↔handle-shared field.
 */
const MLEADER_BODY_HANDLE_REF_KEYS: ReadonlySet<string> = new Set([
  "arrowheadBlock",
]);

/**
 * round-16 BUG 1 — substitute every REFERENCE handle on an entity graph whose
 * lowercased value is a KEY of `idMap` with its mapped value, IN PLACE. This is
 * the SINGLE SOURCE OF TRUTH reference-rewrite used by BOTH:
 *   - the conformant-skeleton record-remint follow pass (`applyHandleRemap` in
 *     dxfBridge.conformantSkeleton delegates to it), and
 *   - the editorStore import-merge collision-rename (storeHelpers): when an
 *     imported entity's `id` is renamed on a case-insensitive host collision, its
 *     SIBLINGS in the same batch that referenced the OLD id (a HATCH
 *     `sourceBoundaryHandles`, a viewport `clippingBoundaryHandle`, an MLEADER
 *     `attDefHandle`, a dimension/reactor ref, an XData 1005) must FOLLOW the
 *     rename or they dangle / resolve to the host's same-spelled handle.
 *
 * It walks the SAME handle-field set as {@link rewriteEntityHandlesGeneric} +
 * `collectHandlesDeep` (the shared {@link isSingleHandleKey} / {@link isHandleArrayKey}
 * shape classifiers + the two `{code, handle}[]` / `{code:1005, value}` carriers),
 * PLUS the {@link MLEADER_BODY_HANDLE_REF_KEYS} escapees (`arrowheadBlock` body-342,
 * which is handle-valued but not `*Handle`-named), so the field coverage cannot
 * DRIFT from those sites — adding a new handle field to the model surfaces here
 * automatically (no hand-enumeration). It substitutes ONLY values present as a key
 * in `idMap` (leaving every other handle verbatim), and DELIBERATELY EXCLUDES the
 * bare own `handle` group-5 identity (a sibling entity's own handle must never be
 * re-stamped to another entity's id) — only `*Handle` reference keys, `*Handles`
 * array members, structural `{code, handle}` pointers, and XData `{code:1005, value}`
 * follow the map.
 *
 * `idMap` keys MUST be lowercased (DXF handles are case-insensitive); the walk
 * probes `value.toLowerCase()`.
 */
export function remapEntityHandleReferences(
  node: unknown,
  idMap: ReadonlyMap<string, string>,
): void {
  if (node === null || typeof node !== "object") return;
  if (Array.isArray(node)) {
    for (const item of node) {
      if (item !== null && typeof item === "object") {
        const obj = item as Record<string, unknown>;
        // A STRUCTURAL `{code, handle}` pointer (viewport layerOverrideHandles):
        // a numeric `code` distinguishes it from an ATTRIB attribute's OWN handle.
        if (typeof obj.code === "number" && typeof obj.handle === "string") {
          const m = idMap.get(obj.handle.toLowerCase());
          if (m !== undefined) obj.handle = m;
        }
        // An XData `{code:1005, value}` handle reference.
        if (obj.code === 1005 && typeof obj.value === "string") {
          const m = idMap.get(obj.value.toLowerCase());
          if (m !== undefined) obj.value = m;
        }
      }
      remapEntityHandleReferences(item, idMap);
    }
    return;
  }
  const record = node as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (
      typeof value === "string" &&
      (isSingleHandleKey(key) || MLEADER_BODY_HANDLE_REF_KEYS.has(key)) &&
      !OWN_HANDLE_KEYS.has(key)
    ) {
      // A `*Handle` REFERENCE field (sibling/owner/structural pointer) OR the
      // MLEADER body-342 `arrowheadBlock` handle escapee (F3). The OWN group-5
      // keys (`handle`, T9b `endblkHandle` / `seqendHandle`) are EXCLUDED (a
      // sibling's own handle must never be re-stamped to another entity's id).
      // Only a value that IS a remap key is
      // substituted, so an authored `arrowheadBlock` NAME is left verbatim.
      const m = idMap.get(value.toLowerCase());
      if (m !== undefined) record[key] = m;
    } else if (
      Array.isArray(value) &&
      isHandleArrayKey(key) &&
      value.every((h) => typeof h === "string")
    ) {
      // A `*Handles` STRING-ARRAY reference field (sourceBoundaryHandles /
      // frozenLayerHandles / reactorHandles / dependencyHandles).
      record[key] = (value as string[]).map((h) =>
        h.length > 0 ? (idMap.get(h.toLowerCase()) ?? h) : h,
      );
    } else if (value !== null && typeof value === "object") {
      remapEntityHandleReferences(value, idMap);
    }
  }
}

// The reserved layout space-block record names whose handles are SKELETON-owned
// and must never be reminted/overwritten by a user/imported record of the same
// name are matched by the canonical `isLayoutBlockName` predicate (imported
// above, SINGLE SOURCE OF TRUTH) — case-insensitive + covers ALL `*Paper_Space<N>`
// suffixes the old hand-listed Set missed (Axis C).

/**
 * Result of {@link remintRecordOwnHandles} — the rewritten record maps + the
 * old-handle → new-handle remint map for every record whose own group-5 handle
 * was minted (empty / non-hex / collision). The caller applies the remint map
 * to references that point AT those records (the entity `ownerHandle` per space
 * is wired separately by the paper reconciliation, but a `blockRecordHandle`
 * MLEADER ref or a `layoutHandle` back-pointer that targets a reminted record
 * must follow it).
 */
export interface RecordHandleRemint {
  blocks: Record<string, { handle?: string; [k: string]: unknown }>;
  blockRecords: Record<string, BlockRecord>;
  layouts: Record<string, Layout>;
  styleTables: DrawingDocument["styleTables"];
  /**
   * T9b — the LTYPE records with a valid, unique own group-5 each (a seeded
   * standard linetype carries none; minted through the SAME `ensure` / `used`
   * set as every other record so nothing aliases). `undefined` when the input
   * had no linetype table.
   */
  linetypes: DrawingDocument["linetypes"] | undefined;
  /** old-record-handle (lowercased) → new minted hex handle. */
  remap: Map<string, string>;
  /**
   * EVERY relocation this pass performed — old-record-handle (lowercased) → new
   * minted hex handle — INCLUDING the ones {@link remap} deliberately withholds
   * because the old value is a SKELETON-OWNED reference target
   * (`skeletonOwnedRefHandles`; a user record colliding with `*Model_Space`'s
   * `17` or the Model layout's `1A`).
   *
   * `remap` answers "which references in THIS document must follow?" and must
   * stay narrow, because a skeleton reference legitimately KEEPS the old value
   * (BUG #849). This map answers the different question "where did the record
   * that used to live at handle X go?", and it is the one a reference imported
   * from ANOTHER drawing needs: a CARRIED OBJECTS record's `340` names a SOURCE
   * object, so it can never be one of the skeleton references `remap` protects,
   * and withholding the relocation from it only leaves it aimed at whatever this
   * document happens to keep at the old handle (@codex round 18 P2).
   */
  relocations: Map<string, string>;
}


/**
 * Mint / collision-check every RECORD-own group-5 handle so a from-scratch BLOCK
 * / BLOCK_RECORD / LAYOUT / style-table record always emits a valid, unique
 * group-5 handle (the writer emits `5 <handle>` only `if (handle)`, so an
 * empty/missing record handle yields NO group-5 → strict ezdxf rejects a
 * BLOCK / BLOCK_RECORD with no handle; an imported hex handle kept verbatim can
 * COLLIDE with a skeleton structural handle on INSERT).
 *
 * A record handle is reminted when it is empty / non-hex / collides with an
 * already-used handle (the skeleton's structural handles + every record handle
 * already assigned in this pass). The `mintHandle` minter is the SHARED minter
 * (seeded from the skeleton + document handle population) so a minted record
 * handle never aliases an entity handle. The returned `remap` lets the caller
 * follow a reminted record from any reference that targeted its old handle.
 *
 * The reserved `*Model_Space` / `*Paper_Space` skeleton records are PRESERVED
 * verbatim (their handles are skeleton-owned + already wired into the per-space
 * entity owner handles) — only USER-named records are minted/collision-checked.
 *
 * T9b — every BLOCK additionally gets an `endblkHandle` (the ENDBLK's own
 * group-5): a reserved skeleton block keeps the template's ENDBLK handle when
 * it carries a valid one (it is skeleton-owned, exactly like its BLOCK handle);
 * every other block (a user block, a minted `*Paper_Space<N>`) is minted /
 * collision-checked. The LTYPE records (`linetypes`) are minted the same way —
 * a seeded standard linetype carries no handle, and R13+ requires one on every
 * symbol-table record.
 */
export function remintRecordOwnHandles(
  blocks: Record<string, { handle?: string; [k: string]: unknown }>,
  blockRecords: Record<string, BlockRecord>,
  layouts: Record<string, Layout>,
  styleTables: DrawingDocument["styleTables"],
  usedHandles: ReadonlySet<string>,
  mintHandle: () => string,
  /**
   * Handles that LEGITIMATELY belong to a SKELETON-SOURCED record (the skeleton's
   * Model/Paper LAYOUT handles + the reconciliation-minted paper records, etc.).
   * These appear in `usedHandles` (via the skeleton-handle seed) yet are NOT a
   * collision for the record that OWNS them — excluding them from the local `used`
   * SEED keeps the skeleton Model layout's own handle (e.g. "1A") from being seen
   * as "used elsewhere" and reminted, which would orphan the ACAD_LAYOUT
   * dictionary 350 pointer (ezdxf would lose the "Model" layout). A USER record
   * whose handle equals a skeleton STRUCTURAL (table-head / dictionary / NON-record)
   * handle is NOT in this set, so it still collides → reminted. This is the BROAD
   * set (skeleton space records + minted `*Paper_Space<N>` + reconciled USER
   * layouts); it is used ONLY for the `used`-seed subtraction below, NOT for the
   * remap exclusion (see {@link skeletonOwnedRefHandles}).
   */
  skeletonRecordHandles: ReadonlySet<string>,
  /**
   * BUG #849 — the NARROW set of handles a NON-USER (SKELETON) reference
   * legitimately points AT: the skeleton SPACE-record handles (`*Model_Space` /
   * `*Paper_Space`, the model/paper ENTITIES' 330 owner) + the TRUE skeleton
   * LAYOUT handles (skeleton `Model` + the phantom `Layout1` reconciliation
   * replaces). When a USER record/layout COLLIDES with one of THESE it still
   * relocates, but the OLD value is one a skeleton reference KEEPS (an entity 330
   * → `*Model_Space`, a skeleton LAYOUT↔BLOCK_RECORD pairing → the Model/Layout1
   * handle), so registering the old value in `remap` would re-home those skeleton
   * refs onto the user record — therefore these are EXCLUDED from `remap`.
   *
   * Distinct from {@link skeletonRecordHandles} (BROAD): it does NOT include the
   * reconciliation-MINTED `*Paper_Space<N>` record handles NOR the reconciled USER
   * layout handles. A USER record colliding with a MINTED/reconciled handle (NOT
   * in this narrow set) STILL registers its old→new in `remap` so its OWN owning
   * BLOCK_RECORD `layoutHandle` (340, set to the OLD value pre-remint) FOLLOWS it
   * — the broad `skeletonRecordHandles` over-suppressed this, ORPHANING the paper
   * layout (BUG #849). It also excludes NOTHING a USER self-ref targets (a
   * table-head handle "8" an `annotativeDecompositions` value / BLOCK `ownerHandle`
   * points at), so those still register + follow (AXIS-B regression guard).
   */
  skeletonOwnedRefHandles: ReadonlySet<string>,
  /** T9b — the document's LTYPE table (name → record); see the doc comment. */
  linetypes: DrawingDocument["linetypes"] | undefined = undefined,
): RecordHandleRemint {
  // Seed the local collision set with `usedHandles` (skeleton structural +
  // entity handles) MINUS the SKELETON-SOURCED record-own handles — a skeleton
  // record's OWN handle is its legitimate owner, NOT a collision against itself.
  // We register each kept record handle back into `used` as we go, so two
  // DIFFERENT records still cannot share a handle.
  const used = new Set<string>();
  for (const h of usedHandles) {
    const lower = h.toLowerCase();
    if (!skeletonRecordHandles.has(lower)) used.add(lower);
  }
  const remap = new Map<string, string>();
  const relocations = new Map<string, string>();

  /**
   * Ensure `current` is a valid, unique hex handle. Returns the handle to use
   * (verbatim when already valid+unique, a fresh mint otherwise) and records the
   * old→new mapping when reminted. `reserved` records (skeleton spaces) are
   * preserved as-is and only registered as used.
   */
  const ensure = (current: string | undefined, reserved: boolean): string => {
    const value = current ?? "";
    const lower = value.toLowerCase();
    if (reserved) {
      if (lower.length > 0) used.add(lower);
      return value;
    }
    const valid = isHexHandle(value) && !used.has(lower);
    if (valid) {
      used.add(lower);
      return value;
    }
    const minted = mintHandle();
    used.add(minted.toLowerCase());
    // BUG #849 (collision-scoping, PRECISE) — register the OLD→new mapping for the
    // GLOBAL rewrite ONLY when the old value is NOT a TRUE SKELETON-OWNED reference
    // target (`skeletonOwnedRefHandles`, the NARROW set). A user record/layout that
    // COLLIDES with a skeleton-owned handle still MOVES to `minted` (its own group-5
    // changes), but the OLD value is one a SKELETON reference legitimately KEEPS:
    //   - case (a): a user BLOCK_RECORD handle "17" == skeleton `*Model_Space` →
    //     EVERY model entity's `ownerHandle` (330) points at "17"; registering
    //     "17"→new would re-home those 330 refs onto the user record. EXCLUDE.
    //   - case (b): a user LAYOUT handle "1A"/"1E" == skeleton Model/Layout1 layout
    //     → the skeleton's own LAYOUT↔BLOCK_RECORD pairing points at it. EXCLUDE.
    // It is registered (FOLLOWED) when the old value is NOT skeleton-owned:
    //   - case (b'): a user LAYOUT colliding with a MINTED `*Paper_Space<N>` /
    //     reconciled USER layout handle (NOT in the narrow set) MUST register so its
    //     OWN owning BLOCK_RECORD `layoutHandle` (340, set to the OLD value
    //     pre-remint) follows the remint via `remapRecordBackRefs` — else the paper
    //     layout is ORPHANED (BUG #849; the old broad `skeletonRecordHandles`
    //     guard suppressed this).
    //   - case (c): a user record's OWN self-reference (`annotativeDecompositions`
    //     value / BLOCK `ownerHandle`) pointing at its own colliding table-head
    //     handle (e.g. "8") is NOT skeleton-owned → still registered + followed
    //     (AXIS-B regression guard).
    if (lower.length > 0) {
      // EVERY relocation, unconditionally — see `RecordHandleRemint.relocations`.
      relocations.set(lower, minted);
      if (!skeletonOwnedRefHandles.has(lower)) remap.set(lower, minted);
    }
    return minted;
  };

  // ── BLOCK_RECORD records ──
  const nextBlockRecords: Record<string, BlockRecord> = {};
  for (const [name, record] of Object.entries(blockRecords)) {
    const reserved = isLayoutBlockName(name);
    const handle = ensure(record.handle, reserved);
    nextBlockRecords[name] = { ...record, handle };
  }

  // ── BLOCK definitions (BLOCKS-section header handle) ──
  const nextBlocks: Record<string, { handle?: string; [k: string]: unknown }> =
    {};
  // T9b — reserved ENDBLK handles claimed in THIS pass: the skeleton
  // `*Paper_Space` template ENDBLK handle may also ride on a clone (a minted
  // `*Paper_Space<N>` built by spreading the skeleton block) — only the FIRST
  // reserved block keeps it verbatim, every later claimant is minted.
  const claimedReservedEndblk = new Set<string>();
  for (const [name, block] of Object.entries(blocks)) {
    const reserved = isLayoutBlockName(name);
    const handle = ensure(block.handle, reserved);
    // T9b — the ENDBLK's own group-5. A reserved skeleton block whose template
    // ENDBLK handle survived the typed import keeps it (skeleton-owned, like its
    // BLOCK handle — it is in `usedHandles` via the skeleton seed, so the
    // non-reserved path would wrongly see it as a collision); a reserved block
    // WITHOUT one (a minted `*Paper_Space<N>`) and every user block go through
    // the collision-checked mint. The BLOCK's own handle is never reused.
    const current =
      typeof block.endblkHandle === "string" ? block.endblkHandle : undefined;
    const keepReserved =
      reserved &&
      current !== undefined &&
      isHexHandle(current) &&
      !claimedReservedEndblk.has(current.toLowerCase());
    let endblkHandle: string;
    if (keepReserved) {
      claimedReservedEndblk.add(current.toLowerCase());
      endblkHandle = ensure(current, true);
    } else {
      endblkHandle = ensure(reserved ? undefined : current, false);
    }
    nextBlocks[name] = { ...block, handle, endblkHandle };
  }

  // ── T9b — LTYPE records (`document.linetypes`, name-keyed). ──
  let nextLinetypes: DrawingDocument["linetypes"] | undefined;
  if (linetypes) {
    nextLinetypes = {};
    for (const [name, lt] of Object.entries(linetypes)) {
      nextLinetypes[name] = { ...lt, handle: ensure(lt.handle, false) };
    }
  }

  // ── LAYOUT objects ──
  const nextLayouts: Record<string, Layout> = {};
  for (const [name, layout] of Object.entries(layouts)) {
    const handle = ensure(layout.handle, /* reserved */ false);
    nextLayouts[name] = { ...layout, handle };
  }

  // ── Style-table records (layers / textStyles / dimensionStyles /
  //    multileaderStyles) — only `layers` + (some) text/dim styles carry a
  //    `handle` field; mint when present + empty/non-hex/colliding. ──
  const nextStyleTables = remintStyleTableHandles(styleTables, ensure);

  // ── Axis B — follow EVERY reminted record handle from EVERY cross-record
  //    back-reference, through ONE generic pass over EVERY reminted record. The
  //    previous two hand-listed loops (LAYOUT.ownerBlockRecordHandle 330 ⇄
  //    BLOCK_RECORD.layoutHandle 340) LEFT STALE: `block.ownerHandle` (330 → its
  //    reminted `*Paper_Space<N>` BLOCK_RECORD — the writer emits a dangling 330)
  //    and `blockRecord.annotativeDecompositions[*]` values (1005 → a reminted
  //    anon `*A_NNN` BLOCK_RECORD). The caller's `applyHandleRemap` walks only
  //    space.entities + block.entities CHILDREN — never a block DEFINITION object
  //    or a record's value-map — so those record-side back-refs would dangle. The
  //    generic pass rewrites every `/Handle$/` field + the `annotativeDecompositions`
  //    value-map on each record; the old 340/330 pair rewrite is now a subset.
  if (remap.size > 0) {
    for (const block of Object.values(nextBlocks)) {
      remapRecordBackRefs(block as Record<string, unknown>, remap);
    }
    for (const record of Object.values(nextBlockRecords)) {
      remapRecordBackRefs(record as Record<string, unknown>, remap);
    }
    for (const layout of Object.values(nextLayouts)) {
      remapRecordBackRefs(layout as Record<string, unknown>, remap);
    }
    for (const layer of nextStyleTables?.layers ?? []) {
      remapRecordBackRefs(layer as Record<string, unknown>, remap);
    }
    // T9b review round 2 (P2) — a COMPLEX LTYPE element (text / shape) carries a
    // `styleHandle` (340 → a STYLE record) NESTED in `elements[]`, which the
    // top-level-only `remapRecordBackRefs` never reaches; the export prefers
    // that raw handle over the name (`resolveStyleHandleForExport`), so a STYLE
    // reminted here left the LTYPE pointing at the OLD handle (dangling / at a
    // colliding skeleton record). Follow the remint through the deep reference
    // walk on a CLONE (the shallow `{...lt}` above still shares `elements` with
    // the caller's document, which must stay untouched).
    if (nextLinetypes) {
      for (const [name, lt] of Object.entries(nextLinetypes)) {
        const followed = structuredClone(lt);
        remapEntityHandleReferences(followed, remap);
        nextLinetypes[name] = followed;
      }
    }
  }

  return {
    blocks: nextBlocks,
    blockRecords: nextBlockRecords,
    layouts: nextLayouts,
    styleTables: nextStyleTables,
    linetypes: nextLinetypes,
    remap,
    relocations,
  };
}

/**
 * Axis B — rewrite EVERY cross-record back-reference on ONE reminted record
 * through `remap` (old-record-handle.toLowerCase() → new minted hex), so a record
 * whose group-5 was reminted is still pointed AT by its siblings:
 *
 *   - every TOP-LEVEL `/Handle$/`-keyed single-handle string field whose
 *     lowercased value is in `remap`. This covers `block.ownerHandle` (330 → its
 *     BLOCK_RECORD), `layout.ownerBlockRecordHandle` (330 → owning BLOCK_RECORD),
 *     `blockRecord.layoutHandle` (340 → LAYOUT). It is a NO-OP for refs whose
 *     target is outside the remint domain (`layout.dictionaryOwnerHandle` → the
 *     stable "D" dict, `blockRecord.extensionDictionaryHandle` /
 *     `blockRecord.reactorHandles` → OBJECTS targets) — `remap` simply has no
 *     entry, so they are left verbatim (and self-heal if the remint scope grows).
 *     The record's OWN group-5 handle is the bare `handle` key (does NOT match
 *     `/Handle$/`), so it is never re-touched here — it was already set by `ensure`.
 *   - the `annotativeDecompositions` value-map (`scaleName → anon *A_NNN
 *     BLOCK_RECORD handle`): each VALUE is a record-side handle ref that must
 *     follow a reminted anon block record.
 *
 * Only operates on the record's OWN top-level fields + the one value-map (NOT a
 * deep recursive walk) — a record's child entities are handled by the caller's
 * `applyHandleRemap`. Mutates the passed record in place (it is already a fresh
 * `{...record}` clone built above). `reactorHandles` is a string[] handle-array;
 * its members are likewise remapped when present so a reactor pointing at a
 * reminted record follows it.
 */
function remapRecordBackRefs(
  record: Record<string, unknown>,
  remap: ReadonlyMap<string, string>,
): void {
  for (const [key, value] of Object.entries(record)) {
    if (
      typeof value === "string" &&
      value.length > 0 &&
      isSingleHandleKey(key) &&
      !OWN_HANDLE_KEYS.has(key)
    ) {
      // A single-handle back-ref (`*Handle`). The OWN keys (bare `handle`, T9b
      // `endblkHandle` / `seqendHandle`) are excluded — they were reminted by
      // `ensure`, not a back-ref to follow.
      const mapped = remap.get(value.toLowerCase());
      if (mapped !== undefined) record[key] = mapped;
      continue;
    }
    if (
      Array.isArray(value) &&
      isHandleArrayKey(key) &&
      value.every((h) => typeof h === "string")
    ) {
      // A string[] handle-array back-ref (`reactorHandles` / `dependencyHandles`):
      // remap each member that targets a reminted record, keep the rest verbatim.
      record[key] = (value as string[]).map((h) =>
        h.length > 0 ? (remap.get(h.toLowerCase()) ?? h) : h,
      );
      continue;
    }
  }
  // The `annotativeDecompositions` value-map (scaleName → anon *A_NNN BLOCK_RECORD
  // handle): rewrite each VALUE that targets a reminted record.
  const decomp = record.annotativeDecompositions;
  if (decomp != null && typeof decomp === "object" && !Array.isArray(decomp)) {
    for (const [scale, handle] of Object.entries(
      decomp as Record<string, unknown>,
    )) {
      if (typeof handle === "string" && handle.length > 0) {
        const mapped = remap.get(handle.toLowerCase());
        if (mapped !== undefined) {
          (decomp as Record<string, string>)[scale] = mapped;
        }
      }
    }
  }
}

/**
 * Re-mint the `handle` field on EVERY layer / text-style / dimension-style
 * record through `ensure` (so it participates in the conformant-skeleton
 * collision-check + old→new remap like every other record). Three tables carry
 * a typed own-handle field: `layerDefinitionSchema` (group 5),
 * `dimensionStyleSchema` (group 105, SL-4) and — since T9b — `textStyleSchema`
 * (group 5). MLEADERSTYLE records are NOT touched: they are OBJECTS-section
 * objects with their own SL-5 handle contract.
 *
 * T9b — a record with an ABSENT / empty / non-hex / colliding handle is MINTED,
 * not left alone. The previous "absent handle is valid" assumption was wrong for
 * R13+: every symbol-table record MUST carry its own handle (Autodesk DXF
 * reference; ezdxf silently re-mints a missing one on load — no audit fix
 * record — and AutoCAD recovers the file), so the from-scratch `0` layer /
 * `Standard` styles were emitted handle-less on every save. Pure: returns a new
 * styleTables object.
 */
function remintStyleTableHandles(
  styleTables: DrawingDocument["styleTables"],
  ensure: (current: string | undefined, reserved: boolean) => string,
): DrawingDocument["styleTables"] {
  if (!styleTables) return styleTables;
  // `ensure` keeps an already valid + unique hex handle verbatim and mints a
  // fresh collision-checked one for an absent / empty / non-hex / colliding one.
  const remint = <T extends { handle?: string }>(record: T): T => ({
    ...record,
    handle: ensure(record.handle, false),
  });
  const next = { ...styleTables };
  if (styleTables.layers) next.layers = styleTables.layers.map(remint);
  // T9b — STYLE records now carry a typed group-5 (`textStyleSchema.handle`).
  if (styleTables.textStyles)
    next.textStyles = styleTables.textStyles.map(remint);
  // SL-4 P1 (@codex #425) — the DIMSTYLE record's own handle (group 105) is now
  // a typed `dimensionStyleSchema.handle` field forwarded to the writer. It MUST
  // pass through the SAME record-remint as layers/block-records/layouts: without
  // this the carried-forward DIMSTYLE handle bypasses the skeleton's handle-remap
  // → it can COLLIDE with a reminted layer/record handle or fall OUTSIDE the
  // remapped hex space (handle-integrity break on the from-scratch / conformant /
  // WBLOCK path). Reminting it here makes the 105 handle unique within the
  // conformant document, exactly like a layer's group-5.
  if (styleTables.dimensionStyles)
    next.dimensionStyles = styleTables.dimensionStyles.map(remint);
  return next;
}
